import { Dispatch, SetStateAction, useCallback } from "react";
import { useSearchParams } from "react-router";

/**
 * Drop-in replacement for `useState<boolean>` that persists the flag as a
 * single search param. Param is set to `openValue` when true, deleted when
 * false. Writes use `{ replace: true }` so the toggle doesn't pollute history.
 */
export default function useUrlFlag(
    name: string,
    openValue = "1",
): [boolean, Dispatch<SetStateAction<boolean>>] {
    const [params, setParams] = useSearchParams();
    const open = params.get(name) === openValue;

    const setOpen: Dispatch<SetStateAction<boolean>> = useCallback(
        (next) =>
            setParams(
                (prev) => {
                    const url = new URLSearchParams(prev);
                    const wasOpen = url.get(name) === openValue;
                    const willOpen = typeof next === "function" ? next(wasOpen) : next;
                    if (willOpen) url.set(name, openValue);
                    else url.delete(name);
                    return url;
                },
                { replace: true },
            ),
        [name, openValue, setParams],
    );

    return [open, setOpen];
}
